import { useState, useEffect, useRef, forwardRef } from "react";
import { useDispatch, connect } from "react-redux";
import { showModal } from "../../../../../../store/actions/modal";
import { cancelScheduleTasks } from "../../../../../../store/actions/group";
import {
  RadioGroup,
  RadioOption,
  StepTitle,
  StepLabel
} from './styles';
import OpenSubscriptions from './components/OpenSubscriptions';
import CloseSubscriptions from './components/CloseSubscriptions';
import WaitingListFields from './components/WaitingListFields';
import ModalConfirmCancelScheduleTask from '../ModalConfirmCancelScheduleTask';
import { SummaryText, SummaryWrapper } from "./components/OpenSubscriptions/styles";

interface DoorProgrammingProps {
  stepNumber: number;
  values: any;
  errors: any;
  touched: any;
  handleChange: (e: any) => void;
  handleBlur: (e: any) => void;
  setFieldValue: (field: string, value: any) => void;
  isEdition?: boolean;
}

const DoorProgramming = forwardRef<HTMLDivElement, DoorProgrammingProps & { cancelScheduleTasksStates?: any }>(({
  stepNumber,
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  isEdition = false,
  cancelScheduleTasksStates
}, ref) => {
  const dispatch = useDispatch();
  const [selectedMode, setSelectedMode] = useState<string>(values.doorMode || 'open_subscriptions');
  const [includeClosing, setIncludeClosing] = useState<boolean>(!!values.subscriptions_closing_date);
  const [openNow, setOpenNow] = useState<boolean>(!values.subscriptions_opening_date);
  const [closeNow, setCloseNow] = useState<boolean>(!values.subscriptions_closing_date);
  const [initialized, setInitialized] = useState<boolean>(false);
  const isDoorsOpen = values.subscriptions_status === 'open';
  const scheduledTasks = Array.isArray(values?.scheduled_tasks) ? values.scheduled_tasks : [];
  const scheduledClosingTask = scheduledTasks.find(
    (task) => task?.status === 'scheduled' && task?.action === 'close_subscriptions'
  );
  const scheduledOpeningTask = scheduledTasks.find(
    (task) => task?.status === 'scheduled' && task?.action === 'open_subscriptions'
  );
  const hasClosingScheduleTask = Boolean(scheduledClosingTask);
  const hasOpeningScheduleTask = Boolean(scheduledOpeningTask);
  const hasAnyDoorScheduleTask = scheduledTasks.some(
    (task) =>
      task?.status === 'scheduled' &&
      (task?.action === 'open_subscriptions' || task?.action === 'close_subscriptions')
  );
  const [showScheduledClosingSummary, setShowScheduledClosingSummary] = useState<boolean>(hasClosingScheduleTask);
  const [showScheduledOpeningSummary, setShowScheduledOpeningSummary] = useState<boolean>(hasOpeningScheduleTask);
  const prevClosingTaskPresenceRef = useRef<boolean>(hasClosingScheduleTask);
  const prevOpeningTaskPresenceRef = useRef<boolean>(hasOpeningScheduleTask);
  const [cancelModalTaskType, setCancelModalTaskType] = useState<'opening' | 'closing'>('opening');
  const [isCancelling, setIsCancelling] = useState<boolean>(false);

  // Sincronizar SOLO al inicio o cuando cambia el modo
  useEffect(() => {
    if (!initialized) {
      const initialIncludeClosing = !!values.subscriptions_closing_date;
      const initialOpenNow = !values.subscriptions_opening_date && selectedMode === 'open_subscriptions';
      const initialCloseNow = !values.subscriptions_closing_date && selectedMode === 'close_subscriptions';

      setIncludeClosing(initialIncludeClosing);
      setOpenNow(initialOpenNow);
      setCloseNow(initialCloseNow);

      // Sincronizar con el formulario
      setFieldValue('includeClosing', initialIncludeClosing);
      setFieldValue('openNow', initialOpenNow);
      setFieldValue('closeNow', initialCloseNow);

      setInitialized(true);
    }
  }, [initialized, values.subscriptions_closing_date, values.subscriptions_opening_date, selectedMode]);

  useEffect(() => {
    // En creación, usar 'open_subscriptions' como valor predeterminado
    if (!isEdition) {
      if (!values.doorMode) {
        setSelectedMode('open_subscriptions');
        setFieldValue('doorMode', 'open_subscriptions');
      }
      return;
    }

    // En edición, inferir el modo basado en el estado actual
    const isDoorsOpen = values.subscriptions_status === 'open';

    if (values.doorMode) {
      setSelectedMode(values.doorMode);
    } else if (isDoorsOpen && hasClosingScheduleTask) {
      // Si las puertas están abiertas y hay tarea de cierre, mostrar modo de cierre
      setSelectedMode('close_subscriptions');
      setFieldValue('doorMode', 'close_subscriptions');
    } else if (!isDoorsOpen && hasOpeningScheduleTask) {
      // Si las puertas están cerradas y hay tarea de apertura, mostrar modo de apertura
      setSelectedMode('open_subscriptions');
      setFieldValue('doorMode', 'open_subscriptions');
    } else if (hasClosingScheduleTask) {
      // Fallback: si hay tarea de cierre (y puertas cerradas), mostrar modo de cierre
      setSelectedMode('close_subscriptions');
      setFieldValue('doorMode', 'close_subscriptions');
    } else if (hasOpeningScheduleTask) {
      // Fallback: si hay tarea de apertura (y puertas abiertas), mostrar modo de apertura
      setSelectedMode('open_subscriptions');
      setFieldValue('doorMode', 'open_subscriptions');
    } else {
      // Inferir modo basado en estado
      setSelectedMode(isDoorsOpen ? 'open_doors' : 'close_doors');
      setFieldValue('doorMode', isDoorsOpen ? 'open_doors' : 'close_doors');
    }
  }, [isEdition, values.subscriptions_status, values.doorMode, hasClosingScheduleTask, hasOpeningScheduleTask]);

  useEffect(() => {
    if (prevClosingTaskPresenceRef.current !== hasClosingScheduleTask) {
      setShowScheduledClosingSummary(hasClosingScheduleTask);
      prevClosingTaskPresenceRef.current = hasClosingScheduleTask;
    }

    if (hasClosingScheduleTask && !showScheduledClosingSummary) {
      console.log("[DoorProgramming] Closing summary hidden despite scheduled task", {
        scheduledTasks,
        selectedMode,
        values,
      });
    }
  }, [hasClosingScheduleTask]);

  useEffect(() => {
    // Sincronizar siempre el estado con hasOpeningScheduleTask
    if (prevOpeningTaskPresenceRef.current !== hasOpeningScheduleTask) {
      setShowScheduledOpeningSummary(hasOpeningScheduleTask);
      prevOpeningTaskPresenceRef.current = hasOpeningScheduleTask;
    }
  }, [hasOpeningScheduleTask, scheduledTasks]);

  // Efecto separado para detectar y corregir desincronizaciones
  useEffect(() => {
    if (hasOpeningScheduleTask && !showScheduledOpeningSummary) {
      console.log("[DoorProgramming] Opening summary hidden despite scheduled task - correcting", {
        scheduledTasks,
        selectedMode,
        hasOpeningScheduleTask,
        showScheduledOpeningSummary,
      });
      // Corregir la desincronización
      setShowScheduledOpeningSummary(true);
      prevOpeningTaskPresenceRef.current = true;
    }
  }, [hasOpeningScheduleTask, scheduledTasks, selectedMode]);

  const handleModeChange = (mode: string) => {
    setSelectedMode(mode);
    setFieldValue('doorMode', mode);

    // Reset states
    setOpenNow(false);
    setCloseNow(false);
    setFieldValue('openNow', false);
    setFieldValue('closeNow', false);

    // Clear dates when switching to keep doors open/closed
    if (mode === 'open_doors' || mode === 'close_doors') {
      setFieldValue('subscriptions_opening_date', '');
      setFieldValue('subscriptions_closing_date', '');
      setIncludeClosing(false);
      setFieldValue('includeClosing', false);
    }
  };

  /**
   * Cancela las schedule tasks y limpia los campos relacionados
   * @param taskType - Tipo de tarea a cancelar ('opening' o 'closing')
   */
  const handleCancelScheduleTask = async (taskType: 'opening' | 'closing') => {
    setIsCancelling(true);
    try {
      // Identificar las tasks a cancelar
      const tasksToCancel = scheduledTasks.filter((task) => {
        if (taskType === 'opening') {
          // Si es apertura, cancelar tanto apertura como cierre
          return task.status === 'scheduled' &&
            (task.action === 'open_subscriptions' || task.action === 'close_subscriptions');
        } else {
          // Si es cierre, solo cancelar la de cierre
          return task.status === 'scheduled' && task.action === 'close_subscriptions';
        }
      });

      // Obtener los IDs de las tasks a cancelar
      const taskIds = tasksToCancel.map((task) => task.id);

      // Dispatch de la acción para cancelar las tasks en el backend
      dispatch(cancelScheduleTasks(taskIds));

      // Actualizar las schedule tasks a status "cancelled" en el estado local
      const updatedTasks = scheduledTasks.map((task) => {
        // Si es apertura, cancelar tanto apertura como cierre
        if (taskType === 'opening') {
          if (task.action === 'open_subscriptions' || task.action === 'close_subscriptions') {
            return { ...task, status: 'cancelled' };
          }
        } else {
          // Si es cierre, solo cancelar la de cierre
          if (task.action === 'close_subscriptions') {
            return { ...task, status: 'cancelled' };
          }
        }
        return task;
      });

      // Actualizar las tasks en el formulario
      setFieldValue('scheduled_tasks', updatedTasks);

      // Limpiar campos de fecha
      if (taskType === 'opening') {
        setFieldValue('subscriptions_opening_date', '');
        setFieldValue('subscriptions_closing_date', ''); // También limpiar cierre si se cancela apertura
        setOpenNow(false);
        setFieldValue('openNow', false);
        setIncludeClosing(false);
        setFieldValue('includeClosing', false);
        setShowScheduledOpeningSummary(false);
        setShowScheduledClosingSummary(false);
        // Cambiar al modo mantener cerradas
        setSelectedMode('close_doors');
        setFieldValue('doorMode', 'close_doors');
      } else {
        setFieldValue('subscriptions_closing_date', '');
        setCloseNow(false);
        setFieldValue('closeNow', false);
        setShowScheduledClosingSummary(false);
        // Si las puertas están abiertas, cambiar a mantener abiertas
        if (isDoorsOpen) {
          setSelectedMode('open_doors');
          setFieldValue('doorMode', 'open_doors');
        }
      }
    } catch (error) {
      console.error('Error cancelling schedule task:', error);
      // Mostrar error al usuario si es necesario
    } finally {
      setIsCancelling(false);
    }
  };

  // Sincronizar el estado de cancelación con el estado de Redux
  useEffect(() => {
    if (cancelScheduleTasksStates?.success) {
      setIsCancelling(false);
    } else if (cancelScheduleTasksStates?.error) {
      console.error('Error cancelling schedule tasks:', cancelScheduleTasksStates.error);
      setIsCancelling(false);
    }
  }, [cancelScheduleTasksStates]);

  useEffect(() => {
    if (
      hasAnyDoorScheduleTask &&
      (selectedMode === 'open_doors' || selectedMode === 'close_doors')
    ) {
      const fallbackMode = isDoorsOpen ? 'close_subscriptions' : 'open_subscriptions';
      setSelectedMode(fallbackMode);
      setFieldValue('doorMode', fallbackMode);
    }
  }, [hasAnyDoorScheduleTask, selectedMode, isDoorsOpen, setFieldValue]);
  const isMembership = values.group_type === 'membership';
  const descriptionText = isMembership
    ? 'Definí en qué momentos tu membresía acepta nuevos miembros.'
    : 'Definí en qué momentos tu taller acepta nuevos miembros.';

  // Debug log
  if (isEdition && !isDoorsOpen && hasAnyDoorScheduleTask) {
    console.log('[DoorProgramming] Debug info:', {
      isEdition,
      isDoorsOpen,
      hasAnyDoorScheduleTask,
      hasOpeningScheduleTask,
      hasClosingScheduleTask,
      showScheduledOpeningSummary,
      selectedMode,
      scheduledTasks: scheduledTasks.map(t => ({ action: t.action, status: t.status })),
    });
  }

  return (
    <div ref={ref}>
      <StepTitle>
        <StepLabel>{stepNumber}. Apertura y cierre de inscripciones</StepLabel>
      </StepTitle>

      <p style={{ marginBottom: '20px', color: '#666' }}>
        {descriptionText}
      </p>

      <RadioGroup>
        {/* ===== MODO CREACIÓN ===== */}
        {!isEdition && (
          <>
            {/* Opción 1: Programar apertura de inscripciones */}
            <RadioOption
              selected={selectedMode === 'open_subscriptions'}
              onClick={() => handleModeChange('open_subscriptions')}
              style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', justifyContent: 'flex-start' }}
            >
              <input
                type="radio"
                name="doorMode"
                value="open_subscriptions"
                checked={selectedMode === 'open_subscriptions'}
                onChange={() => { }}
              />
              <div>
                <strong>Programar apertura de inscripciones</strong>

                {selectedMode === 'open_subscriptions' && (
                  <div onClick={(e) => e.stopPropagation()}>
                    <div style={{ height: 24 }} />
                    <OpenSubscriptions
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      setFieldValue={setFieldValue}
                      includeClosing={includeClosing}
                      setIncludeClosing={setIncludeClosing}
                      openNow={openNow}
                      setOpenNow={setOpenNow}
                      showSummaryOnly={hasOpeningScheduleTask && showScheduledOpeningSummary}
                      onEditScheduledOpening={() => {
                        setShowScheduledOpeningSummary(false);
                        setSelectedMode('open_subscriptions');
                        setFieldValue('doorMode', 'open_subscriptions');
                      }}
                      onCancelScheduledOpening={() => {
                        setCancelModalTaskType('opening');
                        dispatch(showModal('modal-cancel-schedule-task'));
                      }}
                    />
                  </div>
                )}
              </div>
            </RadioOption>

            {/* Opción 2: Mantener inscripciones cerradas */}
            <RadioOption
              selected={selectedMode === 'close_doors'}
              onClick={() => handleModeChange('close_doors')}
              style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', justifyContent: 'flex-start' }}
            >
              <input
                type="radio"
                name="doorMode"
                value="close_doors"
                checked={selectedMode === 'close_doors'}
                onChange={() => { }}
              />

              <div>
                <strong>Inscripciones cerradas</strong>

                {selectedMode === 'close_doors' && (
                  <div onClick={(e) => e.stopPropagation()}>
                    <div style={{ height: 24 }} />
                    <WaitingListFields
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                    />
                  </div>
                )}
              </div>
            </RadioOption>
          </>
        )}

        {/* ===== MODO EDICIÓN - Inscripciones ABIERTAS ===== */}
        {isEdition && isDoorsOpen && (
          <>
            {/* Opción 1: Mantener abiertas */}
            {!hasAnyDoorScheduleTask && (
              <RadioOption
                selected={selectedMode === 'open_doors'}
                onClick={() => handleModeChange('open_doors')}
                style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', justifyContent: 'flex-start' }}
              >
                <input
                  type="radio"
                  name="doorMode"
                  value="open_doors"
                  checked={selectedMode === 'open_doors'}
                  onChange={() => { }}
                />

                <div>
                  <strong>Inscripciones abiertas</strong>
                </div>

                {/* <SummaryWrapper>
                  <SummaryText dangerouslySetInnerHTML={{ __html: `<p>Aceptarás nuevos miembros hasta que se cierre <strong>${isMembership ? 'la membresía' : 'el taller'}</strong>.</p>` }} />
                </SummaryWrapper> */}
              </RadioOption>
            )}

            {/* Opción 2: Cerrar inscripciones */}
            <RadioOption
              selected={selectedMode === 'close_subscriptions'}
              onClick={() => handleModeChange('close_subscriptions')}
              style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', justifyContent: 'flex-start' }}
            >
              <input
                type="radio"
                name="doorMode"
                value="close_subscriptions"
                checked={selectedMode === 'close_subscriptions'}
                onChange={() => { }}
              />
              <div>
                <strong>Cerrar inscripciones</strong>

                {selectedMode === 'close_subscriptions' && (
                  <div onClick={(e) => e.stopPropagation()}>
                    <div style={{ height: 24 }} />
                    <CloseSubscriptions
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      setFieldValue={setFieldValue}
                      closeNow={closeNow}
                      setCloseNow={setCloseNow}
                      showSummaryOnly={hasClosingScheduleTask && showScheduledClosingSummary}
                      onEditScheduledClosing={() => {
                        setShowScheduledClosingSummary(false);
                        setSelectedMode('close_subscriptions');
                        setFieldValue('doorMode', 'close_subscriptions');
                      }}
                      onCancelScheduledClosing={() => {
                        setCancelModalTaskType('closing');
                        dispatch(showModal('modal-cancel-schedule-task'));
                      }}
                    />
                  </div>
                )}
              </div>
            </RadioOption>
          </>
        )}

        {/* ===== MODO EDICIÓN - Inscripciones CERRADAS ===== */}
        {isEdition && !isDoorsOpen && (
          <>
            {/* Opción 1: Mantener cerradas */}
            {!hasAnyDoorScheduleTask && (
              <RadioOption
                selected={selectedMode === 'close_doors'}
                onClick={() => handleModeChange('close_doors')}
                style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', justifyContent: 'flex-start' }}
              >
                <input
                  type="radio"
                  name="doorMode"
                  value="close_doors"
                  checked={selectedMode === 'close_doors'}
                  onChange={() => { }}
                />
                <div>
                  <strong>Inscripciones cerradas</strong>

                  {selectedMode === 'close_doors' && (
                    <div onClick={(e) => e.stopPropagation()}>
                      <div style={{ height: 24 }} />
                      <WaitingListFields
                        values={values}
                        errors={errors}
                        touched={touched}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                      />
                    </div>
                  )}

                  <SummaryWrapper>
                    <SummaryText dangerouslySetInnerHTML={{ __html: `<p>No aceptarás nuevos miembros hasta que se abra <strong>${isMembership ? 'la membresía' : 'el taller'}</strong> nuevamente.</p>` }} />
                  </SummaryWrapper>
                </div>
              </RadioOption>
            )}

            {/* Opción 2: Abrir inscripciones */}
            <RadioOption
              selected={selectedMode === 'open_subscriptions'}
              onClick={() => handleModeChange('open_subscriptions')}
              style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', justifyContent: 'flex-start' }}
            >
              <input
                type="radio"
                name="doorMode"
                value="open_subscriptions"
                checked={selectedMode === 'open_subscriptions'}
                onChange={() => { }}
              />
              <div>
                <strong>Abrir inscripciones</strong>

                {selectedMode === 'open_subscriptions' && (
                  <div onClick={(e) => e.stopPropagation()}>
                    <div style={{ height: 24 }} />
                    <OpenSubscriptions
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      setFieldValue={setFieldValue}
                      includeClosing={includeClosing}
                      setIncludeClosing={setIncludeClosing}
                      openNow={openNow}
                      setOpenNow={setOpenNow}
                      showSummaryOnly={hasOpeningScheduleTask && showScheduledOpeningSummary}
                      onEditScheduledOpening={() => {
                        setShowScheduledOpeningSummary(false);
                        setSelectedMode('open_subscriptions');
                        setFieldValue('doorMode', 'open_subscriptions');
                      }}
                      onCancelScheduledOpening={() => {
                        setCancelModalTaskType('opening');
                        dispatch(showModal('modal-cancel-schedule-task'));
                      }}
                    />
                  </div>
                )}
              </div>
            </RadioOption>
          </>
        )}
      </RadioGroup>

      <ModalConfirmCancelScheduleTask
        isMembership={isMembership}
        onConfirm={() => handleCancelScheduleTask(cancelModalTaskType)}
        taskType={cancelModalTaskType}
        isProcessing={isCancelling}
      />
    </div>
  );
});

DoorProgramming.displayName = 'DoorProgramming';

const state = ({ groupStore }) => ({
  cancelScheduleTasksStates: groupStore.cancelScheduleTasks?.states,
});

export default connect(state)(DoorProgramming);
